import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Archive, Package, CheckCircle, Warning, Eye, Spinner, Truck, MapPin,
  Tag, Plus, Trash, CurrencyDollar, Download, MagnifyingGlass, FileText
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const CURRENCY_SYMBOLS = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };

const ReceiverPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [pendingPOs, setPendingPOs] = useState([]);
  const [receivingRecords, setReceivingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receivingItems, setReceivingItems] = useState([]);
  const [receivingNotes, setReceivingNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [receivingDialogOpen, setReceivingDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewRecord, setViewRecord] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  // Landed cost
  const [landedCost, setLandedCost] = useState({
    actual_freight: '', broker_fees: '', customs_duty: '', insurance: '', trucking: '', other_charges: []
  });

  // Multi-PO receiving
  const [multiPOMode, setMultiPOMode] = useState(false);
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [multiPODialog, setMultiPODialog] = useState(false);
  const [multiPOItems, setMultiPOItems] = useState([]);  // {po_id, po_number, items: [...]}
  const [multiPOStep, setMultiPOStep] = useState(1); // 1=select POs, 2=line items, 3=charges
  const [freightConfig, setFreightConfig] = useState({ freight_allocation_method: 'by_value', weight_unit: 'kg', volume_unit: 'cbm' });

  // Edit Landed Cost dialog (post-receiving, pre-close)
  const [editLandedOpen, setEditLandedOpen] = useState(false);
  const [editLanded, setEditLanded] = useState({ actual_freight: '', trucking: '', broker_fees: '', customs_duty: '', insurance: '', other_charges: [] });
  const [savingLanded, setSavingLanded] = useState(false);

  const isPM = user?.role === 'purchasing_manager' || user?.role === 'system_admin';
  const isAP = user?.role === 'accounts_payable';
  const [apClosedPOs, setApClosedPOs] = useState([]);
  const [apExporting, setApExporting] = useState(null);

  // Invoice upload state for single-PO receiving
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceDocId, setInvoiceDocId] = useState(null);
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  // Invoice upload state for multi-PO receiving (one invoice per PO)
  // Shape: { [po_id]: { file, docId, uploading } }
  const [multiInvoices, setMultiInvoices] = useState({});

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      if (user?.role === 'accounts_payable') {
        const apRes = await axios
          .get(`${API_URL}/api/ap/closed-pos`, getAuthHeaders())
          .catch(() => ({ data: [] }));
        setApClosedPOs(apRes.data || []);
        setLoading(false);
        return;
      }
      const promises = [
        axios.get(`${API_URL}/api/receiver-portal/pending-pos`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/receiving-records`, getAuthHeaders()).catch(() => ({ data: [] }))
      ];
      const [pendingRes, recordsRes] = await Promise.all(promises);
      setPendingPOs(pendingRes.data || []);
      setReceivingRecords(recordsRes.data || []);
      // Fetch freight config
      if (user?.property_ids?.[0]) {
        const cfgRes = await axios.get(`${API_URL}/api/property-settings/freight-config/${user.property_ids[0]}`, getAuthHeaders()).catch(() => ({ data: {} }));
        if (cfgRes.data) setFreightConfig(cfgRes.data);
      }
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [getAuthHeaders, user]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  // Search receiving records
  const searchRecords = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/receiving-records?search=${encodeURIComponent(searchQuery)}`, getAuthHeaders());
      setReceivingRecords(res.data || []);
    } catch (err) { toast.error('Search failed'); }
  };

  const openReceivingDialog = async (po) => {
    try {
      const res = await axios.get(`${API_URL}/api/receiver-portal/po/${po.id}`, getAuthHeaders());
      setSelectedPO(res.data);
      const items = res.data.line_items.map(item => ({
        item_name: item.item_name,
        ordered_quantity: item.quantity,
        received_quantity: '',  // BLANK - Receiver must enter manually
        unit: item.unit || item.unit_of_measure || 'units',
        has_variance: false,
        variance: 0,
        variance_note: '',
        is_inventory_item: item.is_inventory_item || false,
        item_master_id: item.item_master_id || null
      }));
      setReceivingItems(items);
      setReceivingNotes('');
      setLandedCost({ actual_freight: '', broker_fees: '', customs_duty: '', insurance: '', trucking: '', other_charges: [] });
      // Reset vendor invoice
      setInvoiceFile(null);
      setInvoiceDocId(null);
      setReceivingDialogOpen(true);
    } catch (error) { toast.error('Failed to load PO details'); }
  };

  const handleInvoiceFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!selectedPO?.id) { toast.error('Select a PO first'); return; }
    // Validate file type client-side
    const lower = (f.name || '').toLowerCase();
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];
    if (!allowed.some(ext => lower.endsWith(ext))) {
      toast.error('Vendor invoice must be PDF or image (PNG/JPG/JPEG/WEBP)');
      return;
    }
    setInvoiceFile(f);
    setInvoiceUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const headers = getAuthHeaders();
      const res = await axios.post(
        `${API_URL}/api/purchase-orders/${selectedPO.id}/vendor-invoice`,
        formData,
        { ...headers, headers: { ...(headers.headers || {}), 'Content-Type': 'multipart/form-data' } }
      );
      setInvoiceDocId(res.data.document_id);
      toast.success('Vendor invoice uploaded');
    } catch (err) {
      setInvoiceFile(null);
      setInvoiceDocId(null);
      toast.error(err.response?.data?.detail || 'Invoice upload failed');
    } finally {
      setInvoiceUploading(false);
    }
  };

  const updateReceivedQuantity = (index, qty) => {
    const newItems = [...receivingItems];
    const val = qty === '' ? '' : parseFloat(qty);
    newItems[index].received_quantity = val;
    if (val !== '' && !isNaN(val)) {
      newItems[index].variance = newItems[index].ordered_quantity - val;
      newItems[index].has_variance = val !== newItems[index].ordered_quantity;
    } else {
      newItems[index].variance = 0;
      newItems[index].has_variance = false;
    }
    setReceivingItems(newItems);
  };

  const updateVarianceNote = (index, note) => {
    const newItems = [...receivingItems];
    newItems[index].variance_note = note;
    setReceivingItems(newItems);
  };

  const handleConfirmReceiving = async () => {
    // Validate all items have received quantities
    const incomplete = receivingItems.some(item => item.received_quantity === '' || isNaN(item.received_quantity));
    if (incomplete) {
      toast.error('Please enter the received quantity for every line item');
      return;
    }
    // Validate variance notes
    const hasVarianceWithoutNote = receivingItems.some(
      item => item.has_variance && (!item.variance_note || !item.variance_note.trim())
    );
    if (hasVarianceWithoutNote) {
      toast.error('Please provide variance notes for all items with quantity differences');
      return;
    }
    // MANDATORY vendor invoice
    if (!invoiceDocId) {
      toast.error('Vendor Invoice is required before confirming Goods Received');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        line_items: receivingItems.map(item => ({
          ...item,
          received_quantity: parseFloat(item.received_quantity)
        })),
        overall_notes: receivingNotes,
        vendor_invoice_document_id: invoiceDocId,
      };
      if (selectedPO?.po_type === 'overseas') {
        if (landedCost.actual_freight) payload.actual_freight = parseFloat(landedCost.actual_freight);
        if (landedCost.trucking) payload.trucking = parseFloat(landedCost.trucking);
        if (landedCost.broker_fees) payload.broker_fees = parseFloat(landedCost.broker_fees);
        if (landedCost.customs_duty) payload.customs_duty = parseFloat(landedCost.customs_duty);
        if (landedCost.insurance) payload.insurance = parseFloat(landedCost.insurance);
        if (landedCost.other_charges.length > 0) {
          payload.actual_other_charges = landedCost.other_charges.filter(c => c.label && c.amount);
        }
      }

      const res = await axios.post(`${API_URL}/api/purchase-orders/${selectedPO.id}/receive`, payload, getAuthHeaders());
      setReceivingDialogOpen(false);
      setSelectedPO(null);
      setSuccessInfo(res.data);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm receiving');
    } finally { setSubmitting(false); }
  };

  // View receiving record
  const openViewRecord = async (record) => {
    try {
      const res = await axios.get(`${API_URL}/api/receiving-records/${record.id}`, getAuthHeaders());
      setViewRecord(res.data);
      setViewDialogOpen(true);
    } catch (err) { toast.error('Failed to load record'); }
  };

  // Download PDF
  const downloadPdf = async (recordId) => {
    try {
      const res = await axios.get(`${API_URL}/api/receiving-records/${recordId}/pdf`,
        { ...getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `receiving_report.pdf`; a.click();
      toast.success('PDF downloaded');
    } catch (err) { toast.error('Download failed'); }
  };

  // Edit landed cost (post-receiving, pre-PO-close)
  const openEditLanded = () => {
    if (!viewRecord) return;
    setEditLanded({
      actual_freight: viewRecord.actual_freight || '',
      trucking: viewRecord.trucking || '',
      broker_fees: viewRecord.broker_fees || '',
      customs_duty: viewRecord.customs_duty || '',
      insurance: viewRecord.insurance || '',
      other_charges: (viewRecord.actual_other_charges || []).map(c => ({ ...c })),
    });
    setEditLandedOpen(true);
  };
  const updateEditLandedCharge = (idx, field, val) => setEditLanded(prev => ({
    ...prev, other_charges: prev.other_charges.map((c, i) => i === idx ? { ...c, [field]: val } : c)
  }));
  const addEditLandedCharge = () => setEditLanded(prev => ({ ...prev, other_charges: [...prev.other_charges, { label: '', amount: '' }] }));
  const removeEditLandedCharge = (idx) => setEditLanded(prev => ({ ...prev, other_charges: prev.other_charges.filter((_, i) => i !== idx) }));
  const saveLandedCost = async () => {
    setSavingLanded(true);
    try {
      const payload = {
        actual_freight: parseFloat(editLanded.actual_freight) || 0,
        trucking: parseFloat(editLanded.trucking) || 0,
        broker_fees: parseFloat(editLanded.broker_fees) || 0,
        customs_duty: parseFloat(editLanded.customs_duty) || 0,
        insurance: parseFloat(editLanded.insurance) || 0,
        actual_other_charges: editLanded.other_charges
          .filter(c => c.label && c.amount !== '' && c.amount !== null)
          .map(c => ({ label: c.label, amount: parseFloat(c.amount) || 0 })),
      };
      const res = await axios.put(`${API_URL}/api/receiving-records/${viewRecord.id}/landed-cost`, payload, getAuthHeaders());
      toast.success('Landed cost updated');
      setViewRecord(res.data.record);
      setEditLandedOpen(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update landed cost');
    } finally {
      setSavingLanded(false);
    }
  };

  // Landed cost helpers
  const addOtherCharge = () => setLandedCost(prev => ({ ...prev, other_charges: [...prev.other_charges, { label: '', amount: '' }] }));
  const updateOtherCharge = (idx, field, val) => setLandedCost(prev => ({
    ...prev, other_charges: prev.other_charges.map((c, i) => i === idx ? { ...c, [field]: field === 'amount' ? parseFloat(val) || 0 : val } : c)
  }));
  const removeOtherCharge = (idx) => setLandedCost(prev => ({ ...prev, other_charges: prev.other_charges.filter((_, i) => i !== idx) }));
  const calcTotalLanded = () => {
    return (parseFloat(landedCost.actual_freight) || 0) + (parseFloat(landedCost.broker_fees) || 0) +
      (parseFloat(landedCost.customs_duty) || 0) + (parseFloat(landedCost.insurance) || 0) +
      (parseFloat(landedCost.trucking) || 0) +
      landedCost.other_charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  };

  // Multi-PO handlers
  const togglePOSelection = (poId) => {
    setSelectedPOIds(prev => prev.includes(poId) ? prev.filter(id => id !== poId) : [...prev, poId]);
  };

  const startMultiPOReceiving = async () => {
    if (selectedPOIds.length < 2) {
      toast.error('Select at least 2 POs for multi-PO receiving');
      return;
    }
    // Load details for each selected PO
    try {
      const allItems = [];
      for (const poId of selectedPOIds) {
        const res = await axios.get(`${API_URL}/api/receiver-portal/po/${poId}`, getAuthHeaders());
        const po = res.data;
        const items = po.line_items.map(item => ({
          po_id: poId,
          po_number: po.po_number,
          vendor_name: po.vendor_name,
          item_name: item.item_name,
          ordered_quantity: item.quantity,
          received_quantity: '',
          unit: item.unit || item.unit_of_measure || 'units',
          has_variance: false,
          variance_note: '',
          is_inventory_item: item.is_inventory_item || false,
          item_master_id: item.item_master_id || null,
          weight: '',
          volume: ''
        }));
        allItems.push(...items);
      }
      setMultiPOItems(allItems);
      setLandedCost({ actual_freight: '', broker_fees: '', customs_duty: '', insurance: '', trucking: '', other_charges: [] });
      // Reset invoice state for each selected PO
      const initial = {};
      selectedPOIds.forEach(id => { initial[id] = { file: null, docId: null, uploading: false }; });
      setMultiInvoices(initial);
      setMultiPOStep(2);
      setMultiPODialog(true);
    } catch (err) {
      toast.error('Failed to load PO details');
    }
  };

  const handleMultiInvoiceUpload = async (poId, file) => {
    if (!file) return;
    const lower = (file.name || '').toLowerCase();
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];
    if (!allowed.some(ext => lower.endsWith(ext))) {
      toast.error('Vendor invoice must be PDF or image (PNG/JPG/JPEG/WEBP)');
      return;
    }
    setMultiInvoices(prev => ({ ...prev, [poId]: { ...(prev[poId] || {}), file, uploading: true } }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const headers = getAuthHeaders();
      const res = await axios.post(
        `${API_URL}/api/purchase-orders/${poId}/vendor-invoice`,
        formData,
        { ...headers, headers: { ...(headers.headers || {}), 'Content-Type': 'multipart/form-data' } }
      );
      setMultiInvoices(prev => ({ ...prev, [poId]: { file, docId: res.data.document_id, uploading: false } }));
      toast.success(`Invoice attached for ${multiPOItems.find(i => i.po_id === poId)?.po_number || 'PO'}`);
    } catch (err) {
      setMultiInvoices(prev => ({ ...prev, [poId]: { file: null, docId: null, uploading: false } }));
      toast.error(err.response?.data?.detail || 'Invoice upload failed');
    }
  };

  const updateMultiPOItem = (idx, field, value) => {
    setMultiPOItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'received_quantity') {
        const v = value === '' ? '' : parseFloat(value);
        updated.received_quantity = v;
        if (v !== '' && !isNaN(v)) {
          updated.has_variance = v !== updated.ordered_quantity;
        }
      }
      return updated;
    }));
  };

  const handleMultiPOSubmit = async () => {
    // Validate
    const incomplete = multiPOItems.some(i => i.received_quantity === '' || isNaN(i.received_quantity));
    if (incomplete) { toast.error('Enter received quantity for every item'); return; }
    const missingNotes = multiPOItems.some(i => i.has_variance && !i.variance_note.trim());
    if (missingNotes) { toast.error('Provide variance notes for all items with differences'); return; }
    if (freightConfig.freight_allocation_method === 'by_volume') {
      const missingWeight = multiPOItems.some(i => !i.weight);
      if (missingWeight) { toast.error('Enter weight for all items (volume-based allocation)'); return; }
    }
    // MANDATORY: Each PO must have an invoice
    const missingInvoice = selectedPOIds.find(id => !multiInvoices[id]?.docId);
    if (missingInvoice) {
      const poNum = multiPOItems.find(i => i.po_id === missingInvoice)?.po_number || missingInvoice;
      toast.error(`Vendor invoice required for PO ${poNum}`);
      return;
    }
    
    setSubmitting(true);
    try {
      const invoiceMap = {};
      selectedPOIds.forEach(id => { invoiceMap[id] = multiInvoices[id].docId; });
      const payload = {
        po_ids: selectedPOIds,
        line_items: multiPOItems.map(i => ({
          po_id: i.po_id,
          item_name: i.item_name,
          ordered_quantity: i.ordered_quantity,
          received_quantity: parseFloat(i.received_quantity),
          unit: i.unit,
          has_variance: i.has_variance,
          variance_note: i.variance_note,
          is_inventory_item: i.is_inventory_item,
          item_master_id: i.item_master_id,
          weight: i.weight ? parseFloat(i.weight) : null,
          volume: i.volume ? parseFloat(i.volume) : null
        })),
        overall_notes: receivingNotes,
        vendor_invoice_document_ids: invoiceMap,
        actual_freight: parseFloat(landedCost.actual_freight) || undefined,
        trucking: parseFloat(landedCost.trucking) || undefined,
        broker_fees: parseFloat(landedCost.broker_fees) || undefined,
        customs_duty: parseFloat(landedCost.customs_duty) || undefined,
        insurance: parseFloat(landedCost.insurance) || undefined,
        actual_other_charges: landedCost.other_charges.filter(c => c.label && c.amount).length > 0 ? landedCost.other_charges.filter(c => c.label && c.amount) : undefined
      };
      const res = await axios.post(`${API_URL}/api/multi-po-receive`, payload, getAuthHeaders());
      setMultiPODialog(false);
      setMultiPOMode(false);
      setSelectedPOIds([]);
      setSuccessInfo({ ...res.data, receiving_number: res.data.receiving_records?.[0]?.receiving_number, multi: true });
      await fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const filteredRecords = searchQuery.trim()
    ? receivingRecords.filter(r =>
        r.receiving_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : receivingRecords;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#14B8A6] border-t-transparent" /></div>;

  // ==================== ACCOUNTS PAYABLE VIEW ====================
  if (isAP) {
    const exportPackage = async (po) => {
      setApExporting(po.id);
      try {
        const headers = getAuthHeaders();
        const res = await axios.get(
          `${API_URL}/api/ap/purchase-orders/${po.id}/export-package`,
          { ...headers, responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `AP_Package_${po.po_number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('AP Package exported');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Export failed');
      } finally {
        setApExporting(null);
      }
    };
    const apFiltered = searchQuery.trim()
      ? apClosedPOs.filter(p =>
          p.po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.receiving_number?.toLowerCase().includes(searchQuery.toLowerCase()))
      : apClosedPOs;
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0891B2]/10 flex items-center justify-center">
            <FileText size={24} className="text-[#0891B2]" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Accounts Payable — Closed Purchase Orders
            </h1>
            <p className="text-slate-500">Welcome, {user?.full_name}. Export consolidated AP packages.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="ap-closed-pos-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-800">Received & Closed POs ({apClosedPOs.length})</h2>
            <div className="flex items-center gap-2 max-w-sm">
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search PO, vendor, REC #..."
                className="text-sm" data-testid="ap-search-input" />
              <Button variant="outline" size="sm" data-testid="ap-search-btn">
                <MagnifyingGlass size={16} />
              </Button>
            </div>
          </div>
          {apFiltered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Archive size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No closed POs available for AP export.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ap-pos-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">REC #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Received At</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {apFiltered.map(po => (
                    <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`ap-po-row-${po.po_number}`}>
                      <td className="px-4 py-4 font-medium text-slate-800">{po.po_number}</td>
                      <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${po.po_type === 'overseas' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {po.po_type === 'overseas' ? 'Overseas' : 'Local'}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-medium">
                        {CURRENCY_SYMBOLS[po.currency] || '$'}
                        {(po.true_landed_cost || po.total_value || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-slate-600">{po.receiving_number || '—'}</td>
                      <td className="px-4 py-4 text-slate-600">
                        {po.goods_received_at ? format(new Date(po.goods_received_at), 'MMM d, yyyy') : '—'}
                      </td>
                      <td className="px-4 py-4">
                        {po.vendor_invoice_filename ? (
                          <span className="text-xs text-green-700">Attached</span>
                        ) : (
                          <span className="text-xs text-amber-600">Missing</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${po.status === 'closed' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {po.status === 'closed' ? 'Closed' : 'Goods Received'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          size="sm"
                          onClick={() => exportPackage(po)}
                          disabled={apExporting === po.id}
                          className="bg-[#0891B2] hover:bg-[#0E7490] text-white"
                          data-testid={`ap-export-btn-${po.po_number}`}
                        >
                          {apExporting === po.id ? <Spinner size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
                          Export
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center">
          <Archive size={24} className="text-[#14B8A6]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isPM ? 'Receiving Records' : 'Receiver Portal'}
          </h1>
          <p className="text-slate-500">Welcome, {user?.full_name}</p>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isPM && (
          <div onClick={() => setActiveTab('pending')}
            className="bg-gradient-to-br from-[#14B8A6] to-[#2DD4BF] text-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
            data-testid="action-receive-goods">
            <Package size={32} className="mb-3" />
            <h3 className="text-xl font-bold">Receive Goods</h3>
            <p className="text-white/80 text-sm mt-1">{pendingPOs.length} POs ready for receiving</p>
          </div>
        )}
        <div onClick={() => setActiveTab('records')}
          className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover:shadow-lg transition-all"
          data-testid="action-view-records">
          <FileText size={32} className="text-[#14B8A6] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Receiving Records</h3>
          <p className="text-slate-500 text-sm mt-1">{receivingRecords.length} records</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {!isPM && (
          <button onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'pending' ? 'text-[#14B8A6] border-b-2 border-[#14B8A6]' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-pending-receiving">
            <Package size={16} /> POs Ready ({pendingPOs.length})
          </button>
        )}
        <button onClick={() => setActiveTab('records')}
          className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'records' ? 'text-[#14B8A6] border-b-2 border-[#14B8A6]' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-receiving-records">
          <FileText size={16} /> Receiving Records
        </button>
      </div>

      {/* PENDING POs */}
      {activeTab === 'pending' && !isPM && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-[#14B8A6]/5 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">POs Ready for Receiving</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={multiPOMode} onChange={e => { setMultiPOMode(e.target.checked); setSelectedPOIds([]); }}
                  className="rounded" data-testid="multi-po-toggle" />
                <span className="font-medium text-purple-700">Multi-PO Shipment</span>
              </label>
              {multiPOMode && selectedPOIds.length >= 2 && (
                <Button size="sm" onClick={startMultiPOReceiving} className="bg-purple-600 hover:bg-purple-700"
                  data-testid="start-multi-po-btn">
                  Receive {selectedPOIds.length} POs Together
                </Button>
              )}
            </div>
          </div>
          {pendingPOs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
              <p>No POs pending receiving</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {multiPOMode && <th className="px-3 py-3 w-10"></th>}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingPOs.map(po => (
                    <tr key={po.id} className={`hover:bg-slate-50/50 ${selectedPOIds.includes(po.id) ? 'bg-purple-50' : ''}`} data-testid={`receiver-po-row-${po.id}`}>
                      {multiPOMode && (
                        <td className="px-3 py-4">
                          <input type="checkbox" checked={selectedPOIds.includes(po.id)}
                            onChange={() => togglePOSelection(po.id)} className="rounded"
                            data-testid={`select-po-${po.id}`} />
                        </td>
                      )}
                      <td className="px-4 py-4 font-medium text-slate-800">{po.po_number}</td>
                      <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${po.po_type === 'overseas' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {po.po_type === 'overseas' ? <><Truck size={12} className="inline mr-1" />Overseas</> : <><MapPin size={12} className="inline mr-1" />Local</>}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-medium">{CURRENCY_SYMBOLS[po.currency] || '$'}{po.total_value?.toLocaleString()}</td>
                      <td className="px-4 py-4">{po.line_items_count}</td>
                      <td className="px-4 py-4">
                        <Button onClick={() => openReceivingDialog(po)} className="bg-[#14B8A6] hover:bg-[#0D9488]" size="sm"
                          data-testid={`receive-btn-${po.id}`}>
                          <CheckCircle size={16} className="mr-1" /> Receive
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RECEIVING RECORDS */}
      {activeTab === 'records' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="receiving-records-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-800">Receiving Records</h2>
            <div className="flex items-center gap-2 max-w-sm">
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchRecords()}
                placeholder="Search REC-001, PO number, vendor..."
                className="text-sm" data-testid="search-receiving-input" />
              <Button variant="outline" size="sm" onClick={searchRecords} data-testid="search-receiving-btn">
                <MagnifyingGlass size={16} />
              </Button>
            </div>
          </div>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Archive size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No receiving records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="receiving-records-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Receiving #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Received By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Variances</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRecords.map(rec => (
                    <tr key={rec.id} className="hover:bg-slate-50/50" data-testid={`rec-row-${rec.receiving_number}`}>
                      <td className="px-4 py-4">
                        <span className="font-bold text-[#3D1D72]" data-testid={`rec-number-${rec.id}`}>{rec.receiving_number}</span>
                      </td>
                      <td className="px-4 py-4 font-medium text-slate-800">{rec.po_number}</td>
                      <td className="px-4 py-4 text-slate-600">{rec.vendor_name}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rec.po_type === 'overseas' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {rec.po_type?.charAt(0).toUpperCase() + rec.po_type?.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-600">{rec.received_at ? format(new Date(rec.received_at), 'MMM d, yyyy h:mm a') : '-'}</td>
                      <td className="px-4 py-4 text-slate-600">{rec.received_by_name}</td>
                      <td className="px-4 py-4">
                        {rec.has_variances ? <span className="text-red-600 font-medium text-xs">Yes</span> : <span className="text-green-600 text-xs">None</span>}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openViewRecord(rec)} data-testid={`view-rec-${rec.id}`}>
                            <Eye size={14} className="mr-1" /> View
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(rec.id)} data-testid={`download-rec-${rec.id}`}>
                            <Download size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RECEIVING FORM DIALOG */}
      <Dialog open={receivingDialogOpen} onOpenChange={setReceivingDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={24} className="text-[#14B8A6]" />
              Receive Goods — {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* PO Info */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">{selectedPO?.vendor_name}</p>
                <p className="text-sm text-slate-500">{selectedPO?.po_type === 'overseas' ? 'Overseas' : 'Local'} PO</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-lg">{CURRENCY_SYMBOLS[selectedPO?.currency] || '$'}{selectedPO?.total_value?.toLocaleString()}</p>
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Enter the actual quantity received for each item. All items must be completed before submission.
            </p>

            {/* LINE ITEMS TABLE */}
            <div className="border rounded-lg overflow-hidden" data-testid="receiving-line-items-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Item Name</th>
                    <th className="px-3 py-3 text-center font-semibold text-slate-700 w-16">Unit</th>
                    <th className="px-3 py-3 text-center font-semibold text-slate-700 w-24">Qty Ordered</th>
                    <th className="px-3 py-3 text-center font-semibold text-slate-700 w-28 bg-amber-50">Qty Received</th>
                    <th className="px-3 py-3 text-center font-semibold text-slate-700 w-20">Variance</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Variance Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {receivingItems.map((item, idx) => {
                    const variance = item.received_quantity !== '' && !isNaN(item.received_quantity)
                      ? item.ordered_quantity - parseFloat(item.received_quantity) : null;
                    const hasVar = variance !== null && variance !== 0;
                    return (
                      <tr key={idx} className={hasVar ? 'bg-red-50/40' : ''} data-testid={`receiving-item-row-${idx}`}>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            {item.is_inventory_item && <Tag size={14} className="text-[#14B8A6] flex-shrink-0" />}
                            <span className="font-medium text-slate-800">{item.item_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center text-slate-500">{item.unit}</td>
                        <td className="px-3 py-3 text-center font-medium text-slate-700">{item.ordered_quantity}</td>
                        <td className="px-3 py-3 text-center bg-amber-50/50">
                          <Input type="number" min="0" step="0.01"
                            value={item.received_quantity}
                            onChange={e => updateReceivedQuantity(idx, e.target.value)}
                            placeholder="Enter qty"
                            className={`w-24 text-center mx-auto h-8 ${hasVar ? 'border-red-300' : ''}`}
                            data-testid={`received-qty-${idx}`} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          {variance !== null ? (
                            <span className={`font-bold ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-green-600'}`}
                              data-testid={`variance-${idx}`}>
                              {variance === 0 ? '0' : (variance > 0 ? `-${variance}` : `+${Math.abs(variance)}`)}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3">
                          {hasVar ? (
                            <Input value={item.variance_note}
                              onChange={e => updateVarianceNote(idx, e.target.value)}
                              placeholder="Explain variance (required)"
                              className="text-sm border-amber-300 h-8"
                              data-testid={`variance-note-${idx}`} />
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Variance Warning */}
            {receivingItems.some(item => item.has_variance) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Variances Detected</p>
                  <p>Mandatory variance notes required for each item with a quantity difference.</p>
                </div>
              </div>
            )}

            {/* LANDED COST - Overseas POs Only */}
            {selectedPO?.po_type === 'overseas' && (
              <div className="border border-purple-200 rounded-lg overflow-hidden" data-testid="landed-cost-section">
                <div className="bg-purple-50 px-4 py-3 flex items-center gap-2">
                  <CurrencyDollar size={18} className="text-purple-700" />
                  <h3 className="font-semibold text-purple-900 text-sm">Actual Landed Cost</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Freight</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.actual_freight}
                        onChange={e => setLandedCost(prev => ({ ...prev, actual_freight: e.target.value }))} data-testid="landed-freight-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Broker Fees</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.broker_fees}
                        onChange={e => setLandedCost(prev => ({ ...prev, broker_fees: e.target.value }))} data-testid="landed-broker-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Customs Duty</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.customs_duty}
                        onChange={e => setLandedCost(prev => ({ ...prev, customs_duty: e.target.value }))} data-testid="landed-duty-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Insurance</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.insurance}
                        onChange={e => setLandedCost(prev => ({ ...prev, insurance: e.target.value }))} data-testid="landed-insurance-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Trucking</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.trucking}
                        onChange={e => setLandedCost(prev => ({ ...prev, trucking: e.target.value }))} data-testid="landed-trucking-input" />
                    </div>
                  </div>
                  {landedCost.other_charges.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs">Other Charges</Label>
                      {landedCost.other_charges.map((ch, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <Input placeholder="Description" value={ch.label} className="flex-1 h-8 text-sm"
                            onChange={e => updateOtherCharge(idx, 'label', e.target.value)} />
                          <Input type="number" min="0" step="0.01" placeholder="0.00" value={ch.amount} className="w-28 h-8 text-sm"
                            onChange={e => updateOtherCharge(idx, 'amount', e.target.value)} />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeOtherCharge(idx)} className="h-8 w-8 p-0 text-red-500">
                            <Trash size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addOtherCharge} className="text-xs">
                    <Plus size={12} className="mr-1" /> Add Other Charge
                  </Button>
                  <div className="bg-slate-50 rounded-lg p-3 flex justify-between items-center">
                    <div className="text-sm text-slate-600">
                      <p>Total Charges: <span className="font-medium">{CURRENCY_SYMBOLS[selectedPO?.currency] || '$'}{calcTotalLanded().toLocaleString()}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">True Landed Cost</p>
                      <p className="text-lg font-bold text-purple-700" data-testid="true-landed-cost">
                        {CURRENCY_SYMBOLS[selectedPO?.currency] || '$'}{((selectedPO?.subtotal || selectedPO?.total_value || 0) + calcTotalLanded()).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Overall Notes */}
            <div className="space-y-2">
              <Label>Overall Receiving Notes (Optional)</Label>
              <Textarea value={receivingNotes} onChange={e => setReceivingNotes(e.target.value)}
                placeholder="Any additional notes about the receiving..." rows={2} data-testid="receiving-overall-notes" />
            </div>

            {/* MANDATORY VENDOR INVOICE UPLOAD */}
            <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="vendor-invoice-section">
              <div className="bg-red-50 px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-red-900 text-sm flex items-center gap-2">
                  <FileText size={18} /> Vendor Invoice — Required
                </h3>
                {invoiceDocId && <span className="text-xs text-green-700 font-medium">Attached</span>}
              </div>
              <div className="p-4 space-y-2">
                <Label htmlFor="vendor-invoice-file" className="text-xs">
                  Upload the vendor invoice (PDF or image) for this PO. This will be permanently attached to the receiving record.
                </Label>
                <input
                  id="vendor-invoice-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                  onChange={handleInvoiceFileChange}
                  disabled={invoiceUploading}
                  data-testid="vendor-invoice-input"
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-red-600 file:text-white hover:file:bg-red-700"
                />
                {invoiceUploading && <p className="text-xs text-slate-500">Uploading invoice…</p>}
                {invoiceDocId && invoiceFile && (
                  <p className="text-xs text-green-700" data-testid="vendor-invoice-attached">
                    Attached: <span className="font-medium">{invoiceFile.name}</span>
                  </p>
                )}
                {!invoiceDocId && !invoiceUploading && (
                  <p className="text-xs text-red-700">
                    The system will block Goods Received submission until an invoice is attached.
                  </p>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setReceivingDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmReceiving} disabled={submitting || !invoiceDocId || invoiceUploading}
                className="bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="submit-receiving-btn">
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Confirm Goods Received
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SUCCESS CONFIRMATION DIALOG */}
      <Dialog open={!!successInfo} onOpenChange={() => setSuccessInfo(null)}>
        <DialogContent className="max-w-md">
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" weight="fill" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Goods Received!</h2>
            <div className="bg-[#3D1D72]/10 rounded-xl p-4 mb-4">
              <p className="text-xs text-[#3D1D72] uppercase font-medium">Receiving Number</p>
              <p className="text-3xl font-bold text-[#3D1D72]" data-testid="success-receiving-number">
                {successInfo?.receiving_number}
              </p>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {successInfo?.inventory_items_updated > 0 && `${successInfo.inventory_items_updated} inventory item(s) updated. `}
              {successInfo?.has_variances && 'Variances were noted.'}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => { setSuccessInfo(null); setActiveTab('records'); }}>
                View Records
              </Button>
              <Button onClick={() => setSuccessInfo(null)} className="bg-[#14B8A6] hover:bg-[#0D9488]">
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* VIEW RECORD DIALOG */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {viewRecord && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-[#3D1D72]" data-testid="view-receiving-number">{viewRecord.receiving_number}</span>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-600">{viewRecord.po_number}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><p className="text-xs text-slate-500">Vendor</p><p className="font-medium">{viewRecord.vendor_name}</p></div>
                  <div><p className="text-xs text-slate-500">Date & Time</p><p className="font-medium">{viewRecord.received_at ? format(new Date(viewRecord.received_at), 'MMM d, yyyy h:mm a') : '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Receiver</p><p className="font-medium">{viewRecord.received_by_name}</p></div>
                  <div><p className="text-xs text-slate-500">Role</p><p className="font-medium">{viewRecord.received_by_role}</p></div>
                </div>

                {/* Line items */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="view-record-items-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Item Name</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">Unit</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">Qty Ordered</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">Qty Received</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">Variance</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Variance Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {viewRecord.line_items?.map((item, idx) => {
                        const v = (item.ordered_quantity || 0) - (item.received_quantity || 0);
                        return (
                          <tr key={idx} className={v !== 0 ? 'bg-red-50/40' : ''}>
                            <td className="px-3 py-2 font-medium">{item.item_name}</td>
                            <td className="px-3 py-2 text-center text-slate-500">{item.unit}</td>
                            <td className="px-3 py-2 text-center">{item.ordered_quantity}</td>
                            <td className="px-3 py-2 text-center font-medium">{item.received_quantity}</td>
                            <td className={`px-3 py-2 text-center font-bold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-green-600'}`}>
                              {v === 0 ? '0' : (v > 0 ? `-${v}` : `+${Math.abs(v)}`)}
                            </td>
                            <td className="px-3 py-2 text-slate-600 text-sm">{item.variance_note || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Landed cost for overseas */}
                {(viewRecord.true_landed_cost || (viewRecord.po_type === 'overseas' && viewRecord.po_status !== 'closed')) && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-purple-700 uppercase">Actual Landed Cost</p>
                      {viewRecord.po_type === 'overseas' && viewRecord.po_status !== 'closed' && !viewRecord.is_multi_po && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={openEditLanded}
                          className="h-7 px-2 text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                          data-testid="edit-landed-cost-btn"
                        >
                          {viewRecord.true_landed_cost ? 'Edit Landed Cost' : 'Add Landed Cost'}
                        </Button>
                      )}
                    </div>
                    {viewRecord.true_landed_cost ? (
                      <>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {viewRecord.actual_freight > 0 && <div className="flex justify-between"><span>Freight</span><span className="font-medium">${viewRecord.actual_freight}</span></div>}
                          {viewRecord.trucking > 0 && <div className="flex justify-between"><span>Trucking</span><span className="font-medium">${viewRecord.trucking}</span></div>}
                          {viewRecord.broker_fees > 0 && <div className="flex justify-between"><span>Broker Fees</span><span className="font-medium">${viewRecord.broker_fees}</span></div>}
                          {viewRecord.customs_duty > 0 && <div className="flex justify-between"><span>Customs Duty</span><span className="font-medium">${viewRecord.customs_duty}</span></div>}
                          {viewRecord.insurance > 0 && <div className="flex justify-between"><span>Insurance</span><span className="font-medium">${viewRecord.insurance}</span></div>}
                          {viewRecord.actual_other_charges?.map((ch, i) => (
                            <div key={i} className="flex justify-between"><span>{ch.label}</span><span className="font-medium">${ch.amount}</span></div>
                          ))}
                        </div>
                        <div className="border-t border-purple-200 mt-2 pt-2 flex justify-between">
                          <span className="font-bold text-purple-800">True Landed Cost</span>
                          <span className="font-bold text-purple-800 text-base">${viewRecord.true_landed_cost?.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-purple-700">No landed cost charges have been added yet. Use <strong>Add Landed Cost</strong> to record freight, trucking, broker fees, duty, etc.</p>
                    )}
                    {viewRecord.landed_cost_last_edited_by_name && (
                      <p className="mt-2 text-[10px] text-purple-700/80">
                        Last edited by {viewRecord.landed_cost_last_edited_by_name}
                        {viewRecord.landed_cost_last_edited_at ? ` · ${format(new Date(viewRecord.landed_cost_last_edited_at), 'MMM d, yyyy h:mm a')}` : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Per-Item Landed Cost Allocation — visible whenever charges have been allocated to items */}
                {viewRecord.true_landed_cost > 0 && viewRecord.line_items?.some(li =>
                  ((li.allocated_freight || 0) + (li.allocated_trucking || 0) + (li.allocated_broker || 0)
                  + (li.allocated_duty || 0) + (li.allocated_insurance || 0) + (li.allocated_other || 0)) > 0
                ) && (
                  <div className="border border-purple-200 rounded-lg overflow-hidden" data-testid="per-item-landed-allocation">
                    <div className="bg-purple-50 px-3 py-2 flex items-center justify-between">
                      <p className="text-xs font-semibold text-purple-800 uppercase">Per-Item Landed Cost Allocation</p>
                      <p className="text-[10px] text-purple-700/80">
                        Allocated by {viewRecord.allocation_method === 'by_volume' ? 'volume / weight' : 'value'}
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#3D1D72] text-white">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Item</th>
                            <th className="px-2 py-1.5 text-right">Qty</th>
                            <th className="px-2 py-1.5 text-right">Purchase</th>
                            <th className="px-2 py-1.5 text-right">Freight</th>
                            <th className="px-2 py-1.5 text-right">Trucking</th>
                            <th className="px-2 py-1.5 text-right">Broker</th>
                            <th className="px-2 py-1.5 text-right">Duty</th>
                            <th className="px-2 py-1.5 text-right">Insurance</th>
                            <th className="px-2 py-1.5 text-right">Other</th>
                            <th className="px-2 py-1.5 text-right">Total Landed</th>
                            <th className="px-2 py-1.5 text-right">True Unit Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {viewRecord.line_items.map((li, idx) => {
                            const qty = li.received_quantity ?? li.quantity_received ?? li.quantity ?? 0;
                            const purchase = li.purchase_value
                              ?? ((li.unit_price || 0) * qty);
                            return (
                              <tr key={idx} className="hover:bg-purple-50/40" data-testid={`per-item-row-${idx}`}>
                                <td className="px-2 py-1.5 font-medium text-slate-800">{li.item_name}</td>
                                <td className="px-2 py-1.5 text-right">{qty}</td>
                                <td className="px-2 py-1.5 text-right">${(purchase || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_freight || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_trucking || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_broker || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_duty || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_insurance || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${(li.allocated_other || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-semibold text-purple-800">${(li.true_landed_cost || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-semibold text-purple-800">${(li.cost_per_unit_landed || 0).toFixed(4)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="px-3 py-2 text-[10px] text-slate-500 italic bg-slate-50">
                      Freight allocated by {viewRecord.allocation_method === 'by_volume' ? 'volume / weight' : 'value'}; other charges allocated by value. True Unit Cost = Total Landed ÷ Qty.
                    </p>
                  </div>
                )}

                {viewRecord.overall_notes && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Overall Notes</p>
                    <p className="text-sm">{viewRecord.overall_notes}</p>
                  </div>
                )}

                {/* Download */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={() => downloadPdf(viewRecord.id)} className="bg-[#3D1D72] hover:bg-[#2D1652]"
                    data-testid="download-receiving-pdf-btn">
                    <Download size={16} className="mr-2" /> Download Receiving Report PDF
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* MULTI-PO RECEIVING DIALOG */}
      <Dialog open={multiPODialog} onOpenChange={setMultiPODialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={24} className="text-purple-600" />
              Multi-PO Shipment Receiving — {selectedPOIds.length} POs
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Allocation method indicator */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-800">Freight Allocation: {freightConfig.freight_allocation_method === 'by_volume' ? 'By Volume/Weight' : 'By Value'}</p>
              {freightConfig.freight_allocation_method === 'by_volume' && <p className="text-purple-600">Weight ({freightConfig.weight_unit}) and volume ({freightConfig.volume_unit}) required per item</p>}
            </div>

            {multiPOStep >= 2 && (
              <>
                {/* Line Items grouped by PO */}
                <div className="border rounded-lg overflow-hidden" data-testid="multi-po-items-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">PO / Item</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-700 w-16">Unit</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-700 w-20">Ordered</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-700 w-24 bg-amber-50">Received</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-700 w-16">Var</th>
                        {freightConfig.freight_allocation_method === 'by_volume' && <>
                          <th className="px-3 py-2 text-center font-semibold text-slate-700 w-20 bg-blue-50">Weight</th>
                          <th className="px-3 py-2 text-center font-semibold text-slate-700 w-20 bg-blue-50">Volume</th>
                        </>}
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">Variance Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {multiPOItems.map((item, idx) => {
                        const isNewPO = idx === 0 || item.po_id !== multiPOItems[idx - 1].po_id;
                        const variance = item.received_quantity !== '' && !isNaN(item.received_quantity)
                          ? item.ordered_quantity - parseFloat(item.received_quantity) : null;
                        return (
                          <tr key={idx} className={isNewPO ? 'border-t-2 border-purple-200' : ''} data-testid={`multi-item-${idx}`}>
                            <td className="px-3 py-2">
                              {isNewPO && <p className="text-xs font-bold text-purple-600 mb-0.5">{item.po_number} — {item.vendor_name}</p>}
                              <p className="font-medium text-slate-800">{item.item_name}</p>
                            </td>
                            <td className="px-3 py-2 text-center text-xs">{item.unit}</td>
                            <td className="px-3 py-2 text-center">{item.ordered_quantity}</td>
                            <td className="px-3 py-2 text-center bg-amber-50/50">
                              <Input type="number" min="0" step="0.01" value={item.received_quantity}
                                onChange={e => updateMultiPOItem(idx, 'received_quantity', e.target.value)}
                                className="w-20 text-center mx-auto h-7 text-sm" data-testid={`multi-recv-${idx}`} />
                            </td>
                            <td className={`px-3 py-2 text-center font-bold ${variance && variance !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {variance !== null ? (variance === 0 ? '0' : `-${variance}`) : '—'}
                            </td>
                            {freightConfig.freight_allocation_method === 'by_volume' && <>
                              <td className="px-3 py-2 bg-blue-50/50">
                                <Input type="number" min="0" step="0.01" value={item.weight}
                                  onChange={e => updateMultiPOItem(idx, 'weight', e.target.value)}
                                  placeholder={freightConfig.weight_unit} className="w-16 text-center mx-auto h-7 text-xs"
                                  data-testid={`multi-weight-${idx}`} />
                              </td>
                              <td className="px-3 py-2 bg-blue-50/50">
                                <Input type="number" min="0" step="0.01" value={item.volume}
                                  onChange={e => updateMultiPOItem(idx, 'volume', e.target.value)}
                                  placeholder={freightConfig.volume_unit} className="w-16 text-center mx-auto h-7 text-xs"
                                  data-testid={`multi-volume-${idx}`} />
                              </td>
                            </>}
                            <td className="px-3 py-2">
                              {item.has_variance ? (
                                <Input value={item.variance_note} onChange={e => updateMultiPOItem(idx, 'variance_note', e.target.value)}
                                  placeholder="Required" className="h-7 text-xs" data-testid={`multi-varnote-${idx}`} />
                              ) : <span className="text-slate-300 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* MANDATORY VENDOR INVOICES — ONE PER PO */}
                <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="multi-po-invoices-section">
                  <div className="bg-red-50 px-4 py-3">
                    <h3 className="font-semibold text-red-900 text-sm flex items-center gap-2">
                      <FileText size={18} /> Vendor Invoices — One Required per PO
                    </h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {selectedPOIds.map((poId) => {
                      const poNum = multiPOItems.find(i => i.po_id === poId)?.po_number || poId;
                      const vendorName = multiPOItems.find(i => i.po_id === poId)?.vendor_name || '';
                      const inv = multiInvoices[poId] || {};
                      return (
                        <div key={poId} className="flex items-center gap-3 border-b last:border-b-0 pb-3 last:pb-0"
                             data-testid={`multi-invoice-row-${poId}`}>
                          <div className="min-w-[180px]">
                            <p className="text-sm font-semibold text-purple-700">{poNum}</p>
                            <p className="text-xs text-slate-500">{vendorName}</p>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                            onChange={(e) => handleMultiInvoiceUpload(poId, e.target.files?.[0])}
                            disabled={inv.uploading}
                            data-testid={`multi-invoice-input-${poId}`}
                            className="flex-1 text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-red-600 file:text-white hover:file:bg-red-700"
                          />
                          {inv.uploading && <span className="text-xs text-slate-500">Uploading…</span>}
                          {inv.docId && (
                            <span className="text-xs text-green-700 font-medium" data-testid={`multi-invoice-attached-${poId}`}>
                              {inv.file?.name || 'Attached'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Shipment Landed Cost Charges */}
                <div className="border border-purple-200 rounded-lg overflow-hidden" data-testid="multi-po-charges">
                  <div className="bg-purple-50 px-4 py-3">
                    <h3 className="font-semibold text-purple-900 text-sm">Shipment Landed Cost Charges (shared across all {selectedPOIds.length} POs)</h3>
                  </div>
                  <div className="p-4 grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Freight</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.actual_freight}
                        onChange={e => setLandedCost(prev => ({ ...prev, actual_freight: e.target.value }))} data-testid="multi-freight" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Trucking</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.trucking}
                        onChange={e => setLandedCost(prev => ({ ...prev, trucking: e.target.value }))} data-testid="multi-trucking" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Broker Fees</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.broker_fees}
                        onChange={e => setLandedCost(prev => ({ ...prev, broker_fees: e.target.value }))} data-testid="multi-broker" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Customs Duty</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.customs_duty}
                        onChange={e => setLandedCost(prev => ({ ...prev, customs_duty: e.target.value }))} data-testid="multi-duty" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Insurance</Label>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" value={landedCost.insurance}
                        onChange={e => setLandedCost(prev => ({ ...prev, insurance: e.target.value }))} data-testid="multi-insurance" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Total Charges</Label>
                      <p className="text-lg font-bold text-purple-700 mt-1">${calcTotalLanded().toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Overall Notes (Optional)</Label>
                  <Textarea value={receivingNotes} onChange={e => setReceivingNotes(e.target.value)}
                    placeholder="Notes for the entire shipment..." rows={2} data-testid="multi-notes" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setMultiPODialog(false)}>Cancel</Button>
                  <Button
                    onClick={handleMultiPOSubmit}
                    disabled={submitting || selectedPOIds.some(id => !multiInvoices[id]?.docId || multiInvoices[id]?.uploading)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="submit-multi-po-btn"
                  >
                    {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                    Confirm Multi-PO Receiving
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Landed Cost Dialog (post-receiving, pre-PO close, Overseas only) */}
      <Dialog open={editLandedOpen} onOpenChange={setEditLandedOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-landed-cost-dialog">
          <DialogHeader>
            <DialogTitle>{viewRecord?.true_landed_cost ? 'Edit Landed Cost' : 'Add Landed Cost'}</DialogTitle>
            <DialogDescription>
              Enter actual freight, trucking and other charges for {viewRecord?.po_number}. Allocations are recomputed automatically across all line items.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ed-freight">Freight</Label>
                <Input id="ed-freight" type="number" step="0.01" value={editLanded.actual_freight}
                  onChange={(e) => setEditLanded(prev => ({ ...prev, actual_freight: e.target.value }))} placeholder="0.00" data-testid="ed-freight-input" />
              </div>
              <div>
                <Label htmlFor="ed-trucking">Trucking</Label>
                <Input id="ed-trucking" type="number" step="0.01" value={editLanded.trucking}
                  onChange={(e) => setEditLanded(prev => ({ ...prev, trucking: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <Label htmlFor="ed-broker">Broker Fees</Label>
                <Input id="ed-broker" type="number" step="0.01" value={editLanded.broker_fees}
                  onChange={(e) => setEditLanded(prev => ({ ...prev, broker_fees: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <Label htmlFor="ed-duty">Customs Duty</Label>
                <Input id="ed-duty" type="number" step="0.01" value={editLanded.customs_duty}
                  onChange={(e) => setEditLanded(prev => ({ ...prev, customs_duty: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="ed-insurance">Insurance</Label>
                <Input id="ed-insurance" type="number" step="0.01" value={editLanded.insurance}
                  onChange={(e) => setEditLanded(prev => ({ ...prev, insurance: e.target.value }))} placeholder="0.00" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase text-slate-500">Other Charges</Label>
                <Button size="sm" variant="ghost" onClick={addEditLandedCharge} className="h-7 px-2 text-xs">+ Add</Button>
              </div>
              {editLanded.other_charges.map((c, idx) => (
                <div key={idx} className="grid grid-cols-[1fr,120px,40px] gap-2">
                  <Input placeholder="Label (e.g. Storage)" value={c.label} onChange={(e) => updateEditLandedCharge(idx, 'label', e.target.value)} />
                  <Input type="number" step="0.01" placeholder="0.00" value={c.amount} onChange={(e) => updateEditLandedCharge(idx, 'amount', e.target.value)} />
                  <Button variant="ghost" size="sm" onClick={() => removeEditLandedCharge(idx)} className="h-9 px-2 text-red-600">×</Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLandedOpen(false)} disabled={savingLanded}>Cancel</Button>
            <Button onClick={saveLandedCost} disabled={savingLanded} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="save-landed-cost-btn">
              {savingLanded ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiverPortalPage;
